import { hasS3Object } from "@/lib/aws-amplify/storage";
import { type PlanDefault_EditPageQuery } from "@/lib/gql/__generated__/graphql";

import { Converter } from "@/components/features/_shared";
import { convertRequiredPrepMillisecondIntoRequiredPrepMillisecondBeforeDateAndHour } from "@/components/features/_shared/validation-schema/converter";

import { DUMMY_IMAGE } from "@/constants/dummy-image";

import type { ProductPlanFormModel } from "../../../../../type";

export const serverModelToInputModelConverter = (
	serverModel: PlanDefault_EditPageQuery,
): ProductPlanFormModel["InputModel"] => {
	const { plan, customEmailSetting, hotelPolicy, planCustomMailSetting } = serverModel;
	// 初期値が他の設定に依存する可能性がある項目は、ここで初期値を設定する
	const emailTemplateReservation = {
		atCanceled: plan.emailTemplateReservation?.atCanceled ?? customEmailSetting?.reservation?.atCanceled ?? "",
		atCin: plan.emailTemplateReservation?.atCin ?? customEmailSetting?.reservation?.atCin ?? "",
		atCreated: plan.emailTemplateReservation?.atCreated ?? customEmailSetting?.reservation?.atCreated ?? "",
		atUpdated: plan.emailTemplateReservation?.atUpdated ?? customEmailSetting?.reservation?.atUpdated ?? "",
	};
	const checkInStart = plan.checkInStart ?? hotelPolicy?.checkInStart ?? "";
	const checkInEnd = plan.checkInEnd ?? hotelPolicy?.checkInEnd ?? "";
	const checkOut = plan.checkOut ?? hotelPolicy?.checkOut ?? "";

	const { requiredPrepMillisecondBeforeDate, requiredPrepMillisecondBeforeHour } =
		convertRequiredPrepMillisecondIntoRequiredPrepMillisecondBeforeDateAndHour(plan.requiredPrepMillisecond);

	const inputModel: ProductPlanFormModel["InputModel"] = {
		hotelID: plan.hotelID,
		planID: plan.planID,

		name: plan.name,
		mainTarget: plan.mainTarget,

		keyVisuals: plan.keyVisuals,
		/**
		 * 旧版で作成される場合、ダミー画像が設定されない
		 */
		mainKeyVisual: hasS3Object(plan.mainKeyVisual) ? plan.mainKeyVisual : DUMMY_IMAGE.s3Object,

		availableDiscountIDs: plan.availableDiscountIDs,
		availableOptionIDs: plan.availableOptionIDs,
		availableRoomIDs: plan.availableRoomIDs,

		billingTypeTarget: plan.billingTypeTarget,
		billingTypeTerm: plan.billingTypeTerm,

		checkInAvailableDayOfTheWeek: plan.checkInAvailableDayOfTheWeek ?? [],

		checkInEnd,
		checkInStart,
		checkOut,

		coSleepingKidsPrice: plan.coSleepingKidsPrice,

		defaultDiscountID: plan.defaultDiscountID,

		doNotCountKidsAsStock: plan.doNotCountKidsAsStock,

		emailTemplateReservation,

		inSale: plan.inSale,
		isChangeOnlyOption: plan.isChangeOnlyOption,
		isCoSleepingKidsPriceActive: plan.isCoSleepingKidsPriceActive,
		includedMeals: plan.includedMeals,
		isDayTrip: plan.isDayTrip,
		isHiddenHotelQuestions: plan.isHiddenHotelQuestions,
		isNoAddressNeeded: plan.isNoAddressNeeded,
		isPrivate: plan.isPrivate,
		isRequest: plan.isRequest,
		isRoomPriceFree: plan.isRoomPriceFree,
		isSCConnected: plan.isSCConnected,
		isShowRoomForGuest: plan.isShowRoomForGuest,
		isUncancelable: plan.isUncancelable,
		isUnchangeable: plan.isUnchangeable,
		notSendCinMail: plan.notSendCinMail,
		roomChangeDisable: plan.roomChangeDisable,

		kidsPolicy: plan.kidsPolicy?.map(Converter.kidsPolicyItemConverter),
		cancelPolicy: plan.cancelPolicy,

		longStayParams: plan.longStayParams,

		maxDailyStockNum: plan.maxDailyStockNum,
		maxStockNum: plan.maxStockNum,

		minimumPeopleNumPerRoom: plan.minimumPeopleNumPerRoom ?? 1,
		maxPeopleNumPerRoom: plan.maxPeopleNumPerRoom,

		minRoomNum: plan.minRoomNum ?? 1,
		maxRoomNum: plan.maxRoomNum,

		onlyUseCreditPayment: plan.onlyUseCreditPayment,
		onlyUseLocalPayment: plan.onlyUseLocalPayment,

		price: plan.price,

		questions: plan.questions,

		requiredOptionIDs: plan.requiredOptionIDs,

		requiredOptionGroupIDs: plan.requiredOptionGroupIDs,

		requiredPrepMillisecond: plan.requiredPrepMillisecond,

		saleStart: plan.saleStart ?? null,
		saleEnd: plan.saleEnd ?? null,
		saleStartTime: plan.saleStartTime ?? null,
		saleEndTime: plan.saleEndTime ?? null,
		saleStartBeforeDate: plan.saleStartBeforeDate ?? null,

		saleTypeForTerm: plan.saleTypeForTerm,

		shortStayParams: plan.shortStayParams,

		staticPriceParams: plan.staticPriceParams,

		stayableDayOfTheWeek: plan.stayableDayOfTheWeek ?? [],
		isHolidayStayable: plan.isHolidayStayable,
		isHolidayCheckInAvailable: plan.isHolidayCheckInAvailable,
		stayableEnd: plan.stayableEnd,
		stayableStart: plan.stayableStart,

		stockTypeTarget: plan.stockTypeTarget,
		stockTypeTerm: plan.stockTypeTerm,
		// InputModel 固有のプロパティ
		requiredPrepMillisecondBeforeDate,
		requiredPrepMillisecondBeforeHour,
		atCreated: {
			text: planCustomMailSetting?.forReservation.atCreated.text ?? "",
			override: planCustomMailSetting?.forReservation.atCreated.override ?? false,
		},
		atUpdated: {
			text: planCustomMailSetting?.forReservation.atUpdated.text ?? "",
			override: planCustomMailSetting?.forReservation.atUpdated.override ?? false,
		},
		atCanceled: {
			text: planCustomMailSetting?.forReservation.atCanceled.text ?? "",
			override: planCustomMailSetting?.forReservation.atCanceled.override ?? false,
		},
		atCheckInBefore_00days: {
			text: planCustomMailSetting?.forReservation.atCheckInBefore_00days.text ?? "",
			override: planCustomMailSetting?.forReservation.atCheckInBefore_00days.override ?? false,
			shouldSendEmail: planCustomMailSetting?.forReservation.atCheckInBefore_00days.shouldSendEmail ?? false,
		},
		atCheckInBefore_01days: {
			text: planCustomMailSetting?.forReservation.atCheckInBefore_01days.text ?? "",
			override: planCustomMailSetting?.forReservation.atCheckInBefore_01days.override ?? false,
			shouldSendEmail: planCustomMailSetting?.forReservation.atCheckInBefore_01days.shouldSendEmail ?? false,
		},
		atCheckInBefore_07days: {
			text: planCustomMailSetting?.forReservation.atCheckInBefore_07days.text ?? "",
			override: planCustomMailSetting?.forReservation.atCheckInBefore_07days.override ?? false,
			shouldSendEmail: planCustomMailSetting?.forReservation.atCheckInBefore_07days.shouldSendEmail ?? false,
		},
		atCheckOutAfter_01days: {
			text: planCustomMailSetting?.forReservation.atCheckOutAfter_01days.text ?? "",
			override: planCustomMailSetting?.forReservation.atCheckOutAfter_01days.override ?? false,
			shouldSendEmail: planCustomMailSetting?.forReservation.atCheckOutAfter_01days.shouldSendEmail ?? false,
		},
	} satisfies ProductPlanFormModel["InputModel"];

	return inputModel;
};
